import { useContext, useEffect, useState } from 'react';
import { Skeleton } from '../ui/Skeleton';
import { FuelTransactionLine } from './FuelTransactionLine';
import { PeriodContext } from '../../context/PeriodProvider';
import { Message } from '../ui/Message';
import { getTotalPages } from '../../lib/utils';
import { FuelTransaction } from '@/src/types/FuelTransaction';
import { UseQueryResult } from '@tanstack/react-query';

const PAGE_SIZE = 5;

type FuelTransactionWidgetProps = {
    dataFetcher: UseQueryResult<FuelTransaction[], Error>;
};

export const FuelTransactionsWidget = ({ dataFetcher }: FuelTransactionWidgetProps) => {
    const { period } = useContext(PeriodContext);
    const { data, isPending, isError } = dataFetcher;
    const [openFuelTransactionIndex, setOpenFuelTransactionIndex] = useState(0);
    const [page, setPage] = useState(1);

    const toggleOpenFuelTransaction = (index: number) => {
        if (openFuelTransactionIndex !== index) {
            setOpenFuelTransactionIndex(index);
        }
    };

    useEffect(() => {
        setPage(1);
        setOpenFuelTransactionIndex(0);
    }, [period]);

    if (isError) {
        return <Message type="error">Er is iets misgegaan bij het ophalen van uw tankbeurten. Probeer het later nog een keer.</Message>;
    }

    if (isPending) {
        return (
            <>
                {[...Array(6)].map((_, index) => (
                    <Skeleton className="w-full h-[40px] rounded-sm" key={index} />
                ))}
            </>
        );
    }

    if (!data || data.length === 0) {
        return <Message type="info">U heeft dit kwartaal nog geen geregistreerde tankbeurten.</Message>;
    }

    return (
        <>
            {data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((entry: FuelTransaction, index) => {
                const cumulativeIndex = (page - 1) * PAGE_SIZE + index;
                return (
                    <FuelTransactionLine
                        data={entry}
                        key={`${entry.transactionDate}-${index}`}
                        index={cumulativeIndex}
                        toggleOpen={toggleOpenFuelTransaction}
                        isOpen={openFuelTransactionIndex === cumulativeIndex}
                    />
                );
            })}

            <div className="flex justify-center gap-1 mt-3">
                {getTotalPages(data.length, PAGE_SIZE).map((pageNumber) => (
                    <button
                        key={pageNumber}
                        className={`px-1 border-b-2 border- ${page === pageNumber ? 'border-active' : 'border-white'}`}
                        onClick={() => {
                            setPage(pageNumber);

                            setOpenFuelTransactionIndex((pageNumber - 1) * PAGE_SIZE);
                        }}
                    >
                        {pageNumber}
                    </button>
                ))}
            </div>
        </>
    );
};
